import { useState } from 'react';
import { useSectionAlertOverridesList, useDeleteSectionAlertOverride, useAlertRulesList } from '../../../api/hooks';
import { Link } from 'react-router-dom';
import { Button, SortableTh, Table, Td, TH_CLASS } from '../../../components/ui';
import SectionAlertOverrideForm from './SectionAlertOverrideForm';
import type { SectionAlertResolution } from '../../../api/types';
import { useSortableTable, SortColumn } from '../../../lib/useSortableTable';
import { prettifySlug } from '../../../lib/caseStatus';

interface Props { sectionId: number; }

// A rule shows here when this section overrides its on/off state or any threshold.
const hasOverride = (r: SectionAlertResolution): boolean =>
  r.source_per_field.is_enabled === 'section' || Object.values(r.source_per_field.threshold_config).includes('section');

// Single source of truth for the State column's text, shared by the pill and the
// sort accessor so the two can never drift apart.
const stateLabel = (r: SectionAlertResolution): string => (r.is_enabled ? 'Enabled' : 'Disabled');

const thresholdLine = (r: SectionAlertResolution): string =>
  Object.entries(r.threshold_config).map(([k, v]) => `${k} ${String(v)}`).join(' · ') || '—';

function StatePill({ r }: { r: SectionAlertResolution }) {
  return (
    <span className={`inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-semibold ${r.is_enabled ? 'bg-success-bg text-success' : 'bg-danger-bg text-danger-ink'}`}>
      {stateLabel(r)}
    </span>
  );
}

function DeleteOverrideButton({ sectionId, ruleCode }: { sectionId: number; ruleCode: string }) {
  const m = useDeleteSectionAlertOverride(sectionId, ruleCode);
  return (
    <Button variant="ghost" size="sm" onClick={() => m.mutate()}>
      Reset to inherit
    </Button>
  );
}

export default function SectionAlertsTab({ sectionId }: Props) {
  const q = useSectionAlertOverridesList(sectionId);
  const rulesQ = useAlertRulesList();
  const nameOf = (code: string) => rulesQ.data?.rules.find((x) => x.code === code)?.name ?? prettifySlug(code);
  const columns: SortColumn<SectionAlertResolution>[] = [
    { key: 'rule', accessor: (r) => nameOf(r.rule_code) },
    { key: 'state', accessor: (r) => stateLabel(r) },
    { key: 'thresholds', accessor: (r) => thresholdLine(r) },
  ];
  const overridden = (q.data?.rules ?? []).filter(hasOverride);
  const { rows, sort, toggle } = useSortableTable(overridden, columns, { key: 'rule', dir: 'asc' });
  const [editingRule, setEditingRule] = useState<SectionAlertResolution | null>(null);
  if (q.isLoading) return <p className="text-muted">Loading…</p>;
  if (q.isError || !q.data) return <p className="text-danger">Failed to load.</p>;
  const others = q.data.rules.filter((r) => !hasOverride(r));
  return (
    <div>
      <Table minWidth={560}>
        <thead>
          <tr>
            <SortableTh columnKey="rule" sort={sort} onToggle={toggle} className="border-b border-line">Rule</SortableTh>
            <SortableTh columnKey="state" sort={sort} onToggle={toggle} className="border-b border-line">State</SortableTh>
            <SortableTh columnKey="thresholds" sort={sort} onToggle={toggle} className="border-b border-line">Thresholds</SortableTh>
            <th scope="col" className={TH_CLASS}><span className="sr-only">Actions</span></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.rule_code}>
              <Td className="font-semibold text-ink">{nameOf(r.rule_code)}</Td>
              <Td><StatePill r={r} /></Td>
              <Td className="font-mono text-[13px] text-ink">{thresholdLine(r)}</Td>
              <Td align="right">
                <div className="flex justify-end gap-2">
                  <Button variant="ghost" size="sm" onClick={() => setEditingRule(r)}>Edit</Button>
                  <DeleteOverrideButton sectionId={sectionId} ruleCode={r.rule_code} />
                </div>
              </Td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr><td colSpan={4} className="px-2.5 py-4 text-sm text-muted">No overrides yet. Every rule follows the school defaults.</td></tr>
          )}
        </tbody>
      </Table>
      {others.length > 0 && (
        <label className="mt-3 flex flex-wrap items-center gap-2.5 text-[13px] text-muted">
          Add an override for
          <select
            aria-label="Add an override for a rule"
            value=""
            onChange={(e) => setEditingRule(others.find((r) => r.rule_code === e.target.value) ?? null)}
            className="min-h-[36px] rounded-[10px] border border-line-strong bg-surface px-2.5 text-[13px] font-semibold text-ink"
          >
            <option value="">Pick a rule…</option>
            {others.map((r) => <option key={r.rule_code} value={r.rule_code}>{nameOf(r.rule_code)}</option>)}
          </select>
        </label>
      )}
      <p className="mt-3 text-xs text-muted">
        Rules without an override follow the school defaults in{' '}
        <Link to="/settings/alerts" className="font-semibold text-ink hover:underline">Alert rules</Link>.
      </p>
      {editingRule && (
        <SectionAlertOverrideForm
          sectionId={sectionId}
          resolution={editingRule}
          onClose={() => setEditingRule(null)}
        />
      )}
    </div>
  );
}
